/**
 * Shared funding, gifting, and liability helpers for Support at Home suggestion engine.
 */
import type { FactFindAsset, FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import {
    initializeAbpProjectionNomineesOnFactFind,
    markAbpFundedAcquisitionAfterLiquidation,
} from '../../../shared/src/calculation/abpProjectionUtils.ts';
import { calculateAge } from '../utils/index.ts';
import {
    getAustralianFYEndingYearContainingDate,
    buildGiftsByFinancialYear,
    recordDatedGift,
} from '../../../shared/src/calculation/giftingDeprivation.ts';
import {
    GIFTING_LIMIT_ANNUAL,
    GIFTING_LIMIT_5_YEAR,
} from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { DOWNSIZER_CONTRIBUTION_CAP } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { allocateDownsizerContribution } from '../../../shared/src/calculation/downsizerContributionUtils.ts';
import { resolveRadFundingEntry, debitCgtFromOwnershipPool } from '../utils/entityFundingUtils.ts';
import { cardPaymentStyleOf, isCreditCard, liabilityInterestRate } from '../../../shared/src/calculation/support/creditCards.ts';
import { debtsWorthTesting } from './debtsToTest.ts';

export const SAH_EMERGENCY_FUND_RESERVE = 50000;

const FUNDING_LEDGER_KEY = '__sahStrategyFundingLedger';

export interface StrategyFundingLedgerEntry {
    label: string;
    amount: number;
    action: string;
    assetType?: string;
}

export interface ClientLiabilitySummary {
    key: string;
    type: string;
    id: string | number | undefined;
    description: string;
    balance: number;
    interestRate: number;
    /** A credit card paid off in full each month — a monthly bill, never cleared by a plan (debtsToTest.ts). */
    paidInFull?: boolean;
}

/** A single asset to liquidate: `{ type, id, amountToTake }` (plus whatever else the source list carried). */
export interface FundingSource {
    type: string;
    id: string | number | undefined;
    amountToTake: number;
    [key: string]: unknown;
}

export interface LiquidateFundingSourcesOptions {
    preserveBankEmergencyFund?: boolean;
    emergencyFundReserve?: number;
}

export interface LiquidateFundingSourcesResult {
    totalLiquidated: number;
    ledger: StrategyFundingLedgerEntry[];
    cgt: number;
}

export function getStrategyFundingLedger(factFind: DraftFactFind | null | undefined): StrategyFundingLedgerEntry[] {
    return (factFind?.[FUNDING_LEDGER_KEY] as StrategyFundingLedgerEntry[] | undefined) || [];
}

export function attachStrategyFundingLedger(factFind: DraftFactFind, ledger: StrategyFundingLedgerEntry[]): DraftFactFind {
    if (!factFind) return factFind;
    (factFind as Record<string, unknown>)[FUNDING_LEDGER_KEY] = ledger || [];
    return factFind;
}

export function stripStrategyFundingLedger(factFind: DraftFactFind | null | undefined): DraftFactFind | null | undefined {
    if (!factFind) return factFind;
    const clone: DraftFactFind = structuredClone(factFind);
    delete clone[FUNDING_LEDGER_KEY];
    return clone;
}

/**
 * List liabilities with positive balance.
 */
export function listClientLiabilities(factFind: DraftFactFind | null | undefined): ClientLiabilitySummary[] {
    const items: ClientLiabilitySummary[] = [];
    Object.entries(factFind?.liabilities || {}).forEach(([type, list]) => {
        (list || []).forEach((liability) => {
            const balance = liability.balance ?? liability.value ?? 0;
            if (balance > 0) {
                items.push({
                    key: `${type}:${liability.id}`,
                    type,
                    id: liability.id,
                    description: liability.description || type,
                    balance,
                    interestRate: liabilityInterestRate(liability, type),
                    ...(isCreditCard(liability as never, type) && cardPaymentStyleOf(liability as never) === 'full' ? { paidInFull: true } : {}),
                });
            }
        });
    });
    return items.sort((a, b) => b.interestRate - a.interestRate || b.balance - a.balance);
}

/**
 * The debts a Support at Home plan may pay off (strategy/debtsToTest.ts, 26 Oct 2026): not a reverse mortgage,
 * never a card paid off in full each month, any interest-bearing debt, interest-free debts from $5,000.
 */
export function decidableClientLiabilities(factFind: DraftFactFind | null | undefined): ClientLiabilitySummary[] {
    const listed = listClientLiabilities(factFind).filter((l) => {
        const item = ((factFind?.liabilities || {})[l.type] || []).find((x: { id?: unknown }) => String(x?.id) === String(l.id)) as { type?: string } | undefined;
        return item?.type !== 'reverse_mortgage' && l.type !== 'reverseMortgages';
    });
    return debtsWorthTesting(listed);
}

/**
 * Remaining Centrelink gifting capacity given profile history (current FY + 5-year rolling).
 */
export function getRemainingGiftingCapacity(factFind: DraftFactFind | null | undefined, asOfDate: Date = new Date()): number {
    const giftsHistory = factFind?.giftsHistory || {};
    const currentFyEnd = getAustralianFYEndingYearContainingDate(asOfDate);
    const byFy = buildGiftsByFinancialYear(giftsHistory, {});

    const giftedThisFy = byFy.get(currentFyEnd) || 0;
    const annualRemaining = Math.max(0, GIFTING_LIMIT_ANNUAL - giftedThisFy);

    let fiveYearTotal = 0;
    for (let fy = currentFyEnd - 4; fy <= currentFyEnd; fy++) {
        fiveYearTotal += byFy.get(fy) || 0;
    }
    const fiveYearRemaining = Math.max(0, GIFTING_LIMIT_5_YEAR - fiveYearTotal);

    return Math.min(annualRemaining, fiveYearRemaining);
}

/**
 * Record a new gift in giftsHistory (Australian FY key).
 */
export function recordGiftInGiftsHistory(factFind: DraftFactFind, amount: number, asOfDate: Date = new Date()): DraftFactFind {
    const giftAmount = Math.max(0, amount || 0);
    if (giftAmount <= 0) return factFind;

    // Dated, so the five years run from the day it is given (24 Oct 2026).
    recordDatedGift(factFind as never, giftAmount, asOfDate);
    return factFind;
}

/**
 * Liquidate assets per fundingSources plan. Returns total liquidated and ledger lines.
 */
export function liquidateFundingSources(
    factFind: DraftFactFind,
    fundingSources: FundingSource[] | null | undefined,
    options: LiquidateFundingSourcesOptions = {},
): LiquidateFundingSourcesResult {
    const {
        preserveBankEmergencyFund = true,
        emergencyFundReserve = SAH_EMERGENCY_FUND_RESERVE,
    } = options;

    // markAbpFundedAcquisitionAfterLiquidation/initializeAbpProjectionNomineesOnFactFind are the real
    // calc-engine functions (shared/src/calculation/abpProjectionUtils.ts), strictly typed against
    // FactFind/FactFindAsset (plain `number` money fields). By the point they're called here the
    // asset/factFind values being touched have already been coerced to real numbers below, so the cast is
    // safe — TS just can't see that through the wider Draft (`number | string`) declaration.
    initializeAbpProjectionNomineesOnFactFind(factFind as unknown as FactFind);

    const ledger: StrategyFundingLedgerEntry[] = [];
    let totalLiquidated = 0;
    let totalCgt = 0;

    (fundingSources || []).forEach((source) => {
        const take = Math.max(0, source.amountToTake || 0);
        if (take <= 0 || !factFind.assets?.[source.type]) return;

        const asset = factFind.assets[source.type]?.find((a) => String(a.id) === String(source.id));
        if (!asset) return;

        let currentValue = asset.value ?? asset.balance ?? 0;
        if (source.type === 'bankAccounts' && preserveBankEmergencyFund) {
            currentValue = Math.max(0, currentValue - emergencyFundReserve);
        }

        const deduct = Math.min(take, currentValue);
        if (deduct <= 0) return;

        // CGT: selling shares is a CGT event whatever the proceeds fund (gifting, funeral bond, debt repayment,
        // annuity). Compute it on the gross sale (before mutating the holding) and debit it from the bank pool so
        // the projection reflects the tax cost — previously this path omitted CGT entirely.
        if (source.type === 'shares') {
            const entry = resolveRadFundingEntry(factFind, `shares-${asset.id}`, deduct, {});
            if (entry?.cgt > 0) {
                totalCgt += entry.cgt;
                debitCgtFromOwnershipPool(factFind.assets, entry.cgt, asset.ownership);
                ledger.push({ label: 'Capital gains tax (on share sale)', assetType: 'cgt', amount: entry.cgt, action: 'cgt' });
            }
        }

        const rawValue = asset.value ?? asset.balance ?? 0;
        const newRaw = Math.max(0, rawValue - deduct);
        if (asset.value !== undefined) asset.value = newRaw;
        else if (asset.balance !== undefined) asset.balance = newRaw;

        if (source.type === 'accountBasedPensions' && newRaw > 0) {
            markAbpFundedAcquisitionAfterLiquidation(asset as unknown as FactFindAsset, factFind as unknown as FactFind, 1);
        }

        if (newRaw <= 0) {
            const idx = factFind.assets[source.type]?.indexOf(asset) ?? -1;
            if (idx > -1) factFind.assets[source.type]?.splice(idx, 1);
        }

        totalLiquidated += deduct;
        ledger.push({
            label: String(asset.description || asset.name || source.type),
            assetType: source.type,
            amount: deduct,
            action: 'liquidated',
        });
    });

    return { totalLiquidated, ledger, cgt: totalCgt };
}

export function isDownsizerEligible(factFind: DraftFactFind | null | undefined): boolean {
    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) ?? 0 : 0;
    const partnerAge = factFind?.partner?.dob ? calculateAge(factFind.partner.dob) ?? 0 : 0;
    if (factFind?.adviceFor === 'couple') {
        return clientAge >= 55 || partnerAge >= 55;
    }
    return clientAge >= 55;
}

/** Input scenario for buildDownsizeDetailsFromScenario — a candidate downsize configuration. */
export interface DownsizeScenario {
    salePrice?: number;
    sellingCosts?: number;
    newHomePurchasePrice?: number;
    newHomeValue?: number;
    useDownsizer?: boolean;
    clientDownsizerAmount?: number;
    partnerDownsizerAmount?: number;
    repayDebt?: boolean;
    selectedDebtKey?: string | null;
    debtRepayment?: number;
    commenceIncomeStream?: boolean;
    [key: string]: unknown;
}

/** Details consumed by applyDownsizeToFactFind (features/supportAtHome/supportAtHomeFactFindMutators). */
export interface DownsizeDetails {
    salePrice: number;
    sellingCosts: number;
    newHomePurchasePrice: number;
    /** Duty on the new home and the state it is in — optional here; read by the shared downsize path. */
    stampDuty?: number;
    newHomeState?: string;
    downsizerContribution: number;
    partnerDownsizerContribution: number;
    giftingAmount: number;
    debtRepayment: number;
    selectedDebtKey: string | null;
    addedAssets: Record<string, unknown>[];
    commenceIncomeStream: boolean;
    buyUpFunding?: Record<string, number>;
    buyUpCgtOverrides?: Record<string, number>;
    deployment?: Record<string, unknown>;
    /** Sell and rent instead of buying, and the weekly rent — see the shared DownsizeDetails. */
    rentInstead?: boolean;
    rentPerWeek?: number;
    [key: string]: unknown;
}

/**
 * Build downsize details for applyDownsizeToFactFind (SupportAtHomeHelpers).
 */
export function buildDownsizeDetailsFromScenario(factFind: DraftFactFind, scenario: DownsizeScenario): DownsizeDetails {
    const currentHomeValue = factFind.home?.value || 0;
    const salePrice = scenario.salePrice ?? currentHomeValue * 0.975;
    const sellingCosts = scenario.sellingCosts ?? Math.max(0, currentHomeValue - salePrice);
    const newHomePurchasePrice = scenario.newHomePurchasePrice ?? scenario.newHomeValue ?? 0;

    let remainingAfterHome = Math.max(0, salePrice - sellingCosts - newHomePurchasePrice);

    const details: DownsizeDetails = {
        salePrice,
        sellingCosts,
        newHomePurchasePrice,
        downsizerContribution: 0,
        partnerDownsizerContribution: 0,
        giftingAmount: 0,
        debtRepayment: 0,
        selectedDebtKey: scenario.selectedDebtKey || null,
        addedAssets: [],
        commenceIncomeStream: scenario.commenceIncomeStream === true,
    };

    if (scenario.useDownsizer && isDownsizerEligible(factFind)) {
        // Age-aware: fills whichever member's accumulation super is exempt from the means tests
        // (under Age Pension age) first, and keeps the scenario's own per-person ceilings on top.
        // See allocateDownsizerContribution — one rule, shared with RV and the Downsizer module.
        const allocation = allocateDownsizerContribution(factFind as unknown as FactFind, remainingAfterHome, {
            client: scenario.clientDownsizerAmount,
            partner: scenario.partnerDownsizerAmount,
        });
        details.downsizerContribution = allocation.client;
        details.partnerDownsizerContribution = allocation.partner;
        remainingAfterHome -= allocation.client + allocation.partner;
    }

    if (scenario.repayDebt && scenario.selectedDebtKey && (scenario.debtRepayment ?? 0) > 0) {
        details.debtRepayment = Math.min(scenario.debtRepayment ?? 0, remainingAfterHome);
        details.selectedDebtKey = scenario.selectedDebtKey;
        remainingAfterHome -= details.debtRepayment;
    }

    return details;
}
